import type React from 'react';
import { useEffect, useState, Suspense } from 'react';
import { useIntl } from 'react-intl';

import { useLocation, useParams } from '@xing-com/crate-core-router';

import { IndustryCard } from '../../components/industry-card/industry-card';
import { SubpageLayout } from '../../components/layouts/subpage-layout';
import { MetaData } from '../../components/meta-data';
import { NewPagination } from '../../components/new-pagination/new-pagination';
import { NotFound } from '../../components/not-found/not-found';
import { SimpleCompanyCardGrid } from '../../components/simple-company-card-grid/simple-company-card-grid';
import { useCompaniesByIndustry } from '../../hooks/use-companies-by-industry';
import { useCompaniesSegments } from '../../hooks/use-companies-segments/use-companies-segments';
import { useIndustryId } from '../../hooks/use-industry-id/use-industry-id';
import { usePagination } from '../../hooks/use-pagination';
import { useTrackPage } from '../../hooks/use-track-page';
import { createCompaniesIndustries } from '../../models/industry';
import { createSimpleCompanies } from '../../models/simple-company';
import { ROUTES } from '../../routes';
import { getStaticIndustryKey } from '../../utils/static-industry-key';
import {
  pagesTrackingName,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

import * as Styled from './industry-by-companies.styles';
const LIMIT = 78;

type IndustryParams = {
  industry: string;
  segment?: string;
};

const specificDescriptions: { [id: string]: string } = {
  ['160000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_160000',
  ['90000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_90000',
  ['30000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_30000',
  ['40000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_40000',
  ['170000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_170000',
  ['210000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_210000',
  ['20000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_20000',
  ['120000']: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION_120000',
};

const IndustryByCompaniesInner: React.FC<{
  activePage: number;
  trackingPage: string;
}> = ({ activePage, trackingPage }) => {
  const { $t } = useIntl();
  const { industry: industrySEOName, segment: segmentSEOName } =
    useParams<IndustryParams>();
  const { industryId, scrambledIndustryId, scrambleSegmentId, segmentId } =
    useIndustryId({
      industrySEOName,
      segmentSEOName,
    });
  const id = segmentId !== '-1' ? segmentId : industryId;
  const scrambleId =
    scrambleSegmentId !== '-1' ? scrambleSegmentId : scrambledIndustryId;
  const isSegment = !!industrySEOName && !!segmentSEOName;

  const [totalCompanies, setTotalCompanies] = useState(0);
  const trackNwtEvent = useNwtTrackingCompanies();

  const { data: dataSegments } = useCompaniesSegments({
    industryId: scrambleId,
    skip: isSegment,
  });
  const companiesSegments = dataSegments?.companiesSegments?.collection ?? [];

  const industryCards = createCompaniesIndustries(
    // @ts-expect-error graphql generates null values
    companiesSegments,
    industrySEOName
  );
  const hasIndustryCard = industryCards?.length > 0;

  const industryName = $t({ id: getStaticIndustryKey(industryId) });
  const segmentName = $t({ id: getStaticIndustryKey(segmentId) });
  // TODO: Improvement: add Skeleton for the Headline when isLoading, this produces a flicker when
  // the page is loaded first the Headeline is shown as "default" and then fliks to the pageName
  const pageName = isSegment ? segmentName : industryName;

  const breadcumbsNameOverrides = {
    [ROUTES.industry]: () => industryName,
    [ROUTES.segment]: () => segmentName,
  };

  const { data: dataCompaniesByIndustry } = useCompaniesByIndustry({
    id: scrambledIndustryId,
    limit: LIMIT,
    offset: LIMIT * (activePage - 1),
    skip: scrambledIndustryId === '-1',
  });

  useEffect(() => {
    const total = dataCompaniesByIndustry?.companiesByIndustry?.total ?? 0;

    if (totalCompanies !== total) {
      setTotalCompanies(total);
    }

    trackNwtEvent({
      page: 'companies/industries/show',
      eventSchema: 'basic',
      event: 'viewed_screen',
      itemUrn: `urn:x-xing:companies:industry:${id}`,
    });
  }, []);

  const companiesByIndustry =
    dataCompaniesByIndustry?.companiesByIndustry?.collection ?? [];
  // @ts-expect-error graphql generates null values
  const simpleCompanyCards = createSimpleCompanies(companiesByIndustry);
  const hasSomeCard = simpleCompanyCards?.length > 0;

  const hasSpecificDescription = !!specificDescriptions[id];

  const description = hasSpecificDescription
    ? $t({ id: specificDescriptions[id] })
    : $t(
        { id: 'CP_LO_INDUSTRY_COMPANIES_DESCRIPTION' },
        { industry: pageName }
      );

  //Page is not found
  if (id === '-1' || (isSegment && segmentId === '-1')) {
    return <NotFound />;
  }

  return (
    <>
      <SubpageLayout
        headerText={$t(
          { id: 'CP_LO_INDUSTRY_COMPANIES_HEADER' },
          { industry: pageName }
        )}
        description={description}
        overrideBreadcrumbsNames={breadcumbsNameOverrides}
      >
        {hasIndustryCard && (
          <Styled.IndustriesSection>
            <Styled.IndustrySectionHeadline
              size="xxlarge"
              sizeWide="xxxlarge"
              sizeConfined="xxxlarge"
            >
              {$t({
                id: 'CP_LO_INDUSTRY_COMPANIES_SEGMENTS_TITLE',
              })}
            </Styled.IndustrySectionHeadline>
            <Styled.GridIndustries>
              {industryCards.map((card) => (
                <IndustryCard
                  item={card}
                  key={card.id}
                  trackingOptions={{
                    eventSchema: 'basic',
                    event: 'opened',
                    page: 'companies/industries',
                    itemUrn: `surn:x-xing:companies:industry:${card.category}:${card.seoName}`,
                  }}
                />
              ))}
            </Styled.GridIndustries>
          </Styled.IndustriesSection>
        )}
        {hasSomeCard && (
          <>
            <Styled.CompanyCardsHeadline
              size="xxlarge"
              sizeWide="xxxlarge"
              sizeConfined="xxxlarge"
            >
              {$t(
                { id: 'CP_LO_INDUSTRY_COMPANIES_LIST_TITLE' },
                { section: pageName }
              )}
            </Styled.CompanyCardsHeadline>

            <SimpleCompanyCardGrid
              items={simpleCompanyCards}
              trackingPage={trackingPage}
            />
            <NewPagination
              currentPage={activePage}
              perPage={LIMIT}
              total={totalCompanies}
            />
          </>
        )}
      </SubpageLayout>
    </>
  );
};

export const IndustryByCompanies: React.FC = () => {
  const { $t } = useIntl();
  const { activePage, isFirstPage } = usePagination();
  const pageName = pagesTrackingName.industry;
  const trackingPage = 'companies/industries/show';

  const location = useLocation();

  useTrackPage({ pageName });

  return (
    <>
      <MetaData
        index={isFirstPage}
        title={$t({ id: 'CP_LO_INDUSTRY_COMPANIES_META_TITLE' })}
        description={$t({
          id: 'CP_LO_INDUSTRY_COMPANIES_META_DESCRIPTION',
        })}
        canonicalUrl={activePage ? location : undefined}
      />
      <Suspense
        fallback={<SimpleCompanyCardGrid.Skeleton amountOfCards={LIMIT} />}
      >
        <IndustryByCompaniesInner
          activePage={activePage}
          trackingPage={trackingPage}
        />
      </Suspense>
    </>
  );
};
